import { useEffect, useState, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { FileWarning, MessageCircle, PhoneCall, CheckCircle2, X } from "lucide-react";
import api from "@/lib/api";
import { formatBill } from "@/lib/format";
import { toast } from "sonner";
import WhatsAppMessageModal, { buildCollectionReadyMessage } from "@/components/WhatsAppMessageModal";

export default function DocumentPendingDialog({ open, onOpenChange, onCountChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [clearing, setClearing] = useState({});
  const [waRow, setWaRow] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/dashboard/document-pending");
      setItems(data?.items || []);
      if (onCountChange) onCountChange(data?.count || 0);
    } catch { setItems([]); }
    finally { setLoading(false); }
  }, [onCountChange]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const openWhatsApp = (row) => {
    if (!row.mobile) { toast.error("No mobile on record"); return; }
    setWaRow(row);
  };

  const clearRow = async (row) => {
    if (!window.confirm(`Clear document-pending flag for ${formatBill(row.doc_no)}?\n\nThis will remove it from the Document Pending widget. Reason recorded on the receipt stays intact.`)) return;
    setClearing((c) => ({ ...c, [row.id]: true }));
    try {
      await api.patch(`/receipts/${row.id}/document-pending/clear`, { cleared: true });
      toast.success(`Cleared ${formatBill(row.doc_no)} — no longer shown as pending`);
      // Optimistic remove
      setItems((it) => {
        const next = it.filter((x) => x.id !== row.id);
        if (onCountChange) onCountChange(next.length);
        return next;
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not clear");
    } finally {
      setClearing((c) => ({ ...c, [row.id]: false }));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-white max-w-6xl w-[calc(100vw-1.5rem)] sm:w-full max-h-[85vh] overflow-hidden flex flex-col p-0"
        data-testid="document-pending-dialog"
      >
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-red-200 bg-gradient-to-r from-red-50 to-white">
          <DialogTitle className="flex items-center gap-2 text-red-900">
            <FileWarning className="h-5 w-5 text-red-600" />
            Document Pending — Work On Hold
          </DialogTitle>
          <DialogDescription className="text-slate-600">
            {items.length > 0
              ? `${items.length} service${items.length > 1 ? "s" : ""} flagged by operator with a pending reason. Items stay here until an admin, branch or manager clicks "Clear".`
              : loading
              ? "Loading…"
              : "No document-pending items. Nothing on hold."}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-auto px-6 py-4">
          {items.length === 0 && !loading ? (
            <div className="flex flex-col items-center justify-center py-16 text-slate-400">
              <CheckCircle2 className="h-12 w-12 text-emerald-500 mb-3" />
              <div className="text-sm font-semibold text-slate-600">No documents pending</div>
              <div className="text-xs">Nothing is on hold right now.</div>
            </div>
          ) : (
            <table className="w-full text-sm crm-table" data-testid="document-pending-table">
              <thead className="bg-slate-50 text-slate-600 text-[10px] uppercase tracking-wider sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold">Date</th>
                  <th className="px-3 py-2 text-left font-semibold">Doc No</th>
                  <th className="px-3 py-2 text-left font-semibold">Customer</th>
                  <th className="px-3 py-2 text-left font-semibold">Mobile</th>
                  <th className="px-3 py-2 text-left font-semibold">Service</th>
                  <th className="px-3 py-2 text-left font-semibold">App ID</th>
                  <th className="px-3 py-2 text-left font-semibold">Assigned To</th>
                  <th className="px-3 py-2 text-left font-semibold">Login By</th>
                  <th className="px-3 py-2 text-left font-semibold w-80">Pending Reason</th>
                  <th className="px-3 py-2 text-right font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((r) => (
                  <tr key={r.id} data-testid={`document-pending-row-${r.id}`}>
                    <td className="px-3 py-2 font-mono-num text-slate-600 text-xs">{r.doc_date}</td>
                    <td className="px-3 py-2 font-mono-num text-slate-800 font-semibold">{formatBill(r.doc_no)}</td>
                    <td className="px-3 py-2 font-semibold text-slate-800">{r.customer_name || "—"}</td>
                    <td className="px-3 py-2 font-mono-num text-slate-700">{r.mobile || "—"}</td>
                    <td className="px-3 py-2 text-slate-700">{r.service_name || "—"}</td>
                    <td className="px-3 py-2 font-mono-num text-slate-500 text-xs">{r.application_id || "—"}</td>
                    <td className="px-3 py-2 text-xs text-slate-500">{r.operator || "—"}</td>
                    <td className="px-3 py-2 text-xs text-slate-500">{r.supervisor || "—"}</td>
                    <td className="px-3 py-2">
                      <div className="text-xs text-red-800 bg-red-50 border border-red-200 rounded px-2 py-1.5 italic">
                        {r.remark}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="inline-flex items-center gap-1">
                        {r.mobile && (
                          <>
                            <a href={`tel:${r.mobile}`} className="p-1.5 rounded hover:bg-emerald-50 text-emerald-700" title="Call">
                              <PhoneCall className="h-3.5 w-3.5" />
                            </a>
                            <button onClick={() => openWhatsApp(r)} className="p-1.5 rounded hover:bg-emerald-50 text-[#25D366]" title="WhatsApp">
                              <MessageCircle className="h-3.5 w-3.5" />
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => clearRow(r)}
                          disabled={!!clearing[r.id]}
                          className="ml-1 inline-flex items-center gap-1 px-2 py-1 rounded border border-slate-200 bg-white hover:bg-slate-100 text-slate-700 text-xs font-semibold disabled:opacity-60"
                          data-testid={`doc-pending-clear-${r.id}`}
                          title="Clear — remove from Document Pending dashboard"
                        >
                          <X className="h-3.5 w-3.5" /> {clearing[r.id] ? "Clearing…" : "Clear"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="px-6 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-between">
          <div className="text-[11px] text-slate-500">
            Items stay listed here until Cleared. Only admin, branch or manager can Clear.
          </div>
          <Button onClick={() => onOpenChange(false)} className="h-8 bg-[#2E77E6] hover:bg-[#2361C4] text-white text-xs font-semibold" data-testid="document-pending-close-btn">
            Close
          </Button>
        </div>
      </DialogContent>
      {waRow && (
        <WhatsAppMessageModal
          open={!!waRow}
          onOpenChange={(v) => { if (!v) setWaRow(null); }}
          mobile={waRow.mobile}
          customerName={waRow.customer_name}
          subtitle={`Doc ${formatBill(waRow.doc_no)} · ${waRow.service_name || ""}`}
          defaultMessage={buildCollectionReadyMessage(waRow.customer_name)}
          title="WhatsApp Message"
        />
      )}
    </Dialog>
  );
}
